/**
 * User-facing sentences shown when the API sent nothing we can surface.
 *
 * Lives beside — but deliberately apart from — `server-error-message.ts`, which is the entry point
 * that composes this with the server-body extractor. This file is *copy*; that file is a *contract
 * adapter*. They have different lifetimes and different reviewers: when the API standardises on
 * ProblemDetails (issue TBD, see that file's doc comment) the adapter shrinks to a single rung, and
 * this file survives unchanged.
 *
 * Nothing here may ever be bound to `[innerHTML]` — see the render-sink rule in
 * `server-error-message.ts`.
 */

/**
 * The permission-denied sentence, character-identical to the one VisionApi returns as a plain-string
 * 403 body (`PermissionRequiredAuthorizeFilter.cs` and `LearnerController.cs`).
 *
 * It is duplicated on purpose. Twenty-five places in this app, spread over sixteen components
 * (counted 23 September 2026 — re-derive with `grep -rn --include="*.ts" "You do not have permission
 * to complete the requested action" frontend/src | grep -v spec.ts | grep -c "=="`), compare an error
 * string against it with `===` and upgrade the generic denial into a specific one
 * ("You do not have permission to add a note"). If this fallback used a second, near-identical
 * wording, those branches would silently stop matching whenever the API sent no body.
 * Reuse-before-you-build applies to copy as much as to code.
 *
 * There is a knock-on, accepted deliberately (#1634): a 403 that did *not* come from VisionApi — an
 * Azure or firewall block page — does have a body, but it is HTML, so the markup guard in
 * `server-error-message.ts` rejects it and this sentence is what those twenty-five comparisons then
 * see. Someone who does hold the permission can be told, specifically, that they do not. That is rare,
 * and it still reads as a sentence rather than a dumped block page. The alternative — using this
 * wording only when the body was genuinely empty — would lose the specific message on a real API
 * refusal whose body we could not lift, which is the commoner case. Anyone adding a twenty-sixth
 * comparison should know that is the trade.
 *
 * The two cases can still be told apart, just not by the sentence — and only in the affected user's own
 * browser console, because the staff site sends no telemetry. The error interceptor logs `HTTP 403 <path>`
 * for both. When no usable server sentence was found (`resolveServerErrorMessage` reports
 * `source: 'generic'`), it appends `(no usable server message; body …)` naming the body's shape — `type: string`
 * for a block page, `type: null` or similar for an empty body. A refusal the API worded itself (`'server'`)
 * gets no suffix, and a validation body gets a different one. Note that `'generic'` therefore also covers a
 * real API refusal that arrived with an empty body, not only a block page.
 *
 * Changing this wording is a two-repo change.
 */
export const FORBIDDEN_MESSAGE = 'You do not have permission to complete the requested action.';

/**
 * Shown instead of the field messages when the API rejects a form. The individual messages are not
 * surfaced: their values echo whatever was submitted, and their keys are C# property names.
 *
 * "Check and try again" is right here, unlike the general case below — correcting the input genuinely
 * does change the outcome.
 */
export const VALIDATION_MESSAGE = 'Some of the details entered were not valid. Please check and try again.';

/**
 * Shown when there is no status to go on at all — a handler was thrown something that is not an HTTP
 * response. Named rather than written as `genericMessageForStatus(0)`, because the two mean different
 * things: 0 is "the request never completed", this is "we have no idea". Editing the status-0 copy
 * should not silently change what an unknown throw says.
 */
export const UNKNOWN_ERROR_MESSAGE = 'Something went wrong. If it keeps happening, please contact support.';

/**
 * A short, plain-English sentence for a failure whose response carried no message we could use.
 *
 * The general case deliberately carries no *retry* instruction: the status most often reaching here
 * is 400, where repeating the identical request fails identically. Where retrying is genuinely the
 * right next step — a timeout — it says so. The support escalation on the general case is deliberate;
 * callers must not append a second next step of their own, or the two read in sequence.
 */
export function genericMessageForStatus(status: number): string {
  switch (status) {
    case 0:
      // No response reached us at all — offline, a dropped connection, or a cancelled request. Telling
      // this user to contact support sends them to the service desk for something they can fix in five
      // seconds, and costs a ticket. The interceptor separately raises the app's connection banner for
      // this status; this is the sentence that lands in the handler's own snackbar.
      return 'We could not reach the server. Check your connection and try again.';
    case 403:
      return FORBIDDEN_MESSAGE;
    case 404:
      return 'We could not find what you asked for.';
    case 408:
    case 504:
      // The one situation where repeating the same request is the right thing to do.
      return 'The request took too long. Please try again.';
    case 413:
      // Not only file uploads — a bulk save can exceed the limit too — so give both a next step
      // rather than a conditional that half the readers find does not apply to them.
      return 'That request was too large. Try a smaller file, or save fewer records at a time.';
    default:
      // Same wording as UNKNOWN_ERROR_MESSAGE, deliberately NOT the same constant — see its doc
      // comment. "No status we recognise" and "not an HTTP failure at all" are different claims that
      // happen to read the same today; collapsing them recouples what that comment separates.
      return 'Something went wrong. If it keeps happening, please contact support.';
  }
}
